import { apiClient } from './client';
import { API_ENDPOINTS } from '@/shared/constants/api/endpoints.ts';
import type {
  AuthUser,
  CheckAvailabilityRequest,
  CheckAvailabilityResponse,
  LoginRequest,
  LoginResponse,
  MessageResponse,
  RegisterRequest,
  RegisterResponse,
  RequestPasswordResetRequest,
  SignupRecoveryEmailRequest,
  SignupRecoveryLookupRequest,
  FoundLoginId,
  OnboardingState,
  ConsentItem,
  ConsentState,
} from '@/shared/types/auth.type';

/**
 * auth-service 엔드포인트 호출부.
 * 경로는 API_ENDPOINTS(단일 레지스트리)에서 가져온다.
 * 쿠키는 apiClient(withCredentials=true)가 자동 전송/수신한다.
 */
export const authApi = {
  // ── 가입/로그인/리프레시/로그아웃 ──────────────────────
  signup: (body: RegisterRequest) =>
    apiClient
      .post<RegisterResponse>(API_ENDPOINTS.auth.signup, body)
      .then((r) => r.data),

  /** 가입 위저드 계정 스텝 → '다음' 진입 전 이메일/아이디 중복 확인(비로그인). */
  checkAvailability: (body: CheckAvailabilityRequest) =>
    apiClient
      .post<CheckAvailabilityResponse>(
        API_ENDPOINTS.auth.checkAvailability,
        body,
        { skipAuthRedirect: true },
      )
      .then((r) => r.data),

  login: (body: LoginRequest) =>
    apiClient
      .post<LoginResponse>(API_ENDPOINTS.auth.login, body)
      .then((r) => r.data),

  /**
   * 명시적 refresh 호출이 필요한 경우에만 사용.
   * 일반적인 401 자동 재발급은 api-client 인터셉터가 담당한다.
   * 쿠키 모드: 서버가 set-cookie로 새 토큰을 내려주고 body는 비어있음 → void.
   */
  refresh: () =>
    apiClient.post<void>(API_ENDPOINTS.auth.refresh).then((r) => r.data),

  logout: () =>
    apiClient
      .post<{ ok: boolean }>(API_ENDPOINTS.auth.logout)
      .then((r) => r.data),

  me: () =>
    apiClient.get<AuthUser>(API_ENDPOINTS.auth.me).then((r) => r.data),

  // ── 이메일 인증 ────────────────────────────────────────
  verifyEmail: (token: string) =>
    apiClient
      .post<MessageResponse>(API_ENDPOINTS.auth.verifyEmail, { token })
      .then((r) => r.data),

  resendVerification: () =>
    apiClient
      .post<MessageResponse>(API_ENDPOINTS.auth.resendVerification)
      .then((r) => r.data),

  /**
   * 비로그인 재발송 — 가입완료 화면 이탈 후, 혹은 로그인 시 EMAIL_NOT_VERIFIED 응답을 받은 사용자가 호출.
   * 백엔드는 email 만 받고 서버측 lookup 으로 INACTIVE 사용자에게만 발송.
   * (기존: AuthenticatedUser 전체를 body 로 보냈으나, 위조 가능 이슈로 email-only 로 교체)
   */
  resendVerificationAnonymous: (email: string) =>
    apiClient
      .post<MessageResponse>(
        API_ENDPOINTS.auth.resendVerificationAnonymous,
        { email },
        { skipAuthRedirect: true },
      )
      .then((r) => r.data),

  /**
   * 로그인 화면 미인증 안내의 재발송. 로그인 폼의 아이디·비밀번호로 계정을 특정해
   * **가입된 주소로** 보낸다 — 오타로 가입했다면 사용자는 그 주소를 모르기 때문.
   */
  resendVerificationByLogin: (body: LoginRequest) =>
    apiClient
      .post<MessageResponse>(API_ENDPOINTS.auth.resendVerificationByLogin, body, {
        skipAuthRedirect: true,
      })
      .then((r) => r.data),

  /**
   * 가입정보 확인 — 미인증 계정의 가입 이메일 조회. 선행: phone/send-otp → phone/verify-otp.
   * 서버는 OTP proof 를 확인만 하고 소비하지 않는다(이어지는 signupRecoveryEmail 이 소비).
   */
  signupRecoveryLookup: (body: SignupRecoveryLookupRequest) =>
    apiClient
      .post<{ email: string }>(API_ENDPOINTS.auth.signupRecoveryLookup, body, {
        skipAuthRedirect: true,
      })
      .then((r) => r.data),

  /** 가입 이메일 재설정 + 새 주소로 인증 메일 발송. 오타 주소로 간 링크는 서버가 무효화한다. */
  signupRecoveryEmail: (body: SignupRecoveryEmailRequest) =>
    apiClient
      .post<MessageResponse>(API_ENDPOINTS.auth.signupRecoveryEmail, body, {
        skipAuthRedirect: true,
      })
      .then((r) => r.data),

  // ── 비밀번호 재설정 ───────────────────────────────────
  requestPasswordReset: (body: RequestPasswordResetRequest) =>
    apiClient
      .post<MessageResponse>(API_ENDPOINTS.auth.requestPasswordReset, body)
      .then((r) => r.data),

  resetPassword: (token: string, newPassword: string) =>
    apiClient
      .post<MessageResponse>(API_ENDPOINTS.auth.resetPassword, {
        token,
        newPassword,
      })
      .then((r) => r.data),

  // ── 아이디 찾기 ───────────────────────────────────────
  /**
   * 휴대폰으로 아이디 찾기. 선행: phone/send-otp → phone/verify-otp.
   * 서버가 OTP proof 를 소비해 번호 소유를 확인한 뒤에만 목록을 돌려준다.
   */
  findLoginIdByPhone: (phone: string) =>
    apiClient
      .post<{ accounts: FoundLoginId[] }>(
        API_ENDPOINTS.auth.findLoginIdByPhone,
        { phone },
        { skipAuthRedirect: true },
      )
      .then((r) => r.data),

  /** 이메일로 아이디 받기 — 결과는 응답이 아니라 그 주소로 발송된다. */
  findLoginIdByEmail: (email: string) =>
    apiClient
      .post<MessageResponse>(
        API_ENDPOINTS.auth.findLoginIdByEmail,
        { email },
        { skipAuthRedirect: true },
      )
      .then((r) => r.data),

  // ── 첫 로그인 온보딩 ──────────────────────────────────
  onboardingState: () =>
    apiClient
      .get<OnboardingState>(API_ENDPOINTS.auth.onboardingState)
      .then((r) => r.data),

  /** 이관 계정 아이디 재설정 — 지금 아이디를 그대로 보내면 확정만 한다. */
  onboardingLoginId: (loginId: string) =>
    apiClient
      .post<OnboardingState>(API_ENDPOINTS.auth.onboardingLoginId, {
        loginId,
      })
      .then((r) => r.data),

  /** 성공 응답이 새 세션 쿠키를 함께 내려준다 — 다른 기기 세션만 끊기고 이 화면은 이어진다. */
  onboardingPassword: (body: { newPassword: string }) =>
    apiClient
      .post<OnboardingState>(API_ENDPOINTS.auth.onboardingPassword, body)
      .then((r) => r.data),

  /** 인증번호 메일 발송. `email` 은 구버전 이관 때 이메일이 없던 계정만 — 새 주소로 바꾼 뒤 그 주소로 보낸다. */
  onboardingEmail: (email?: string) =>
    apiClient
      .post<{ email: string }>(
        API_ENDPOINTS.auth.onboardingEmail,
        email ? { email } : {},
      )
      .then((r) => r.data),

  onboardingEmailVerify: (code: string) =>
    apiClient
      .post<OnboardingState>(API_ENDPOINTS.auth.onboardingEmailVerify, {
        code,
      })
      .then((r) => r.data),

  onboardingPhone: (body: { phone: string }) =>
    apiClient
      .post<OnboardingState>(API_ENDPOINTS.auth.onboardingPhone, body)
      .then((r) => r.data),

  onboardingPhoneDefer: () =>
    apiClient
      .post<OnboardingState>(API_ENDPOINTS.auth.onboardingPhoneDefer)
      .then((r) => r.data),

  // ── 약관 동의 (온보딩 마지막 단계 · 개정 후 재동의) ────
  consentState: () =>
    apiClient
      .get<ConsentState>(API_ENDPOINTS.auth.consents)
      .then((r) => r.data),

  /** 동의 제출 — 성공 후 토큰을 갱신해야 status·재동의 클레임이 새 토큰에 반영된다. */
  submitConsents: (items: ConsentItem[]) =>
    apiClient
      .post<ConsentState>(API_ENDPOINTS.auth.consents, { items })
      .then((r) => r.data),

  // ── 이메일 변경 ───────────────────────────────────────
  /** 새 이메일로 확인 링크 발송 (인증 필요 + 현재 비밀번호 재확인) */
  requestEmailChange: (newEmail: string, currentPassword: string) =>
    apiClient
      .post<MessageResponse>(API_ENDPOINTS.auth.requestEmailChange, {
        newEmail,
        currentPassword,
      })
      .then((r) => r.data),

  /** 메일 링크의 토큰으로 이메일 변경 확정 (공개) */
  confirmEmailChange: (token: string) =>
    apiClient
      .post<MessageResponse>(
        API_ENDPOINTS.auth.confirmEmailChange,
        { token },
        { skipAuthRedirect: true },
      )
      .then((r) => r.data),

  // ── 휴대폰 SMS OTP (솔라피) ─────────────────────────────
  /** 인증번호 발송. (가입 전 단계 → skipAuthRedirect) */
  sendPhoneOtp: (phone: string) =>
    apiClient
      .post<MessageResponse>(
        API_ENDPOINTS.auth.sendPhoneOtp,
        { phone },
        { skipAuthRedirect: true },
      )
      .then((r) => r.data),

  /**
   * 인증번호 확인. 성공 시 서버가 검증 증표(proof)를 번호 기준으로 저장(10분).
   * 클라는 토큰을 받지 않고, 이후 가입/번호변경에 같은 번호를 보내면 서버가 proof를 소비한다.
   */
  verifyPhoneOtp: (phone: string, code: string) =>
    apiClient
      .post<MessageResponse>(
        API_ENDPOINTS.auth.verifyPhoneOtp,
        { phone, code },
        { skipAuthRedirect: true },
      )
      .then((r) => r.data),
};
